import React, { forwardRef } from 'react';
import ReportPaper from './report/ReportPaper';
import ReportMarkedFigure from './report/ReportMarkedFigure';
import paper from './report/ReportPaper.module.css';
import styles from './ApplicationMapReportTemplate.module.css';
import { APPLICATION_VIEWS, APPLICATION_VIEW_OPTIONS, getApplicationFigure } from '../../pages/appointment/components/applicationMap/applicationFigures';
import { formatAmount, formatDate, markKindLabel, totalsByProduct } from '../../pages/appointment/components/applicationMap/applicationMarks';

// Rosto é mais largo que o corpo; os dois cabem ao lado da tabela
const FIGURE_WIDTH = { [APPLICATION_VIEWS.FACE]: 190, [APPLICATION_VIEWS.FRONT]: 150, [APPLICATION_VIEWS.BACK]: 150 };

const missing = <span className={paper.muted}>—</span>;

/** Subtítulo e data da assinatura do mapa (`job` = { record, variant, photoFigures }). */
export function applicationMapReportMeta({ record }) {
  const count = ((record && record.marks) || []).length;
  const date = formatDate(record && record.markedAt);
  return {
    subtitle: `${date ? `Atendimento de ${date} · ` : ''}${count} ${count === 1 ? 'marcação' : 'marcações'}`,
    date,
  };
}

/**
 * Miolo do PDF: totais por produto, cada vista com marcações (desenho ou foto
 * do paciente recortada) com os números e a quantidade de cada uma, a lista
 * das marcações, lote e observações.
 *
 * `record`: { markedAt, marks, batch, notes }; `variant`: figura (male | female);
 * `photoFigures`: { [view]: { url, width, height, crop } } das vistas com foto
 * de fundo já baixada (sem entrada, a vista usa o desenho).
 */
export const ApplicationMapReportBody = ({ record, variant, photoFigures }) => {
  const marks = (record && record.marks) || [];
  const totals = totalsByProduct(marks);

  const views = APPLICATION_VIEW_OPTIONS
    .map((option) => ({
      ...option,
      items: marks
        .map((mark, index) => ({ mark, number: index + 1 }))
        .filter(({ mark }) => mark.view === option.key),
    }))
    .filter((view) => view.items.length > 0);

  function renderFigure(view) {
    const photo = (photoFigures || {})[view.key];
    const width = FIGURE_WIDTH[view.key] || 150;
    const figureMarks = view.items.map(({ mark, number }) => ({
      key: `m-${number}`,
      x: mark.x,
      y: mark.y,
      x2: mark.x2,
      y2: mark.y2,
      kind: mark.kind,
      color: mark.color,
      number,
      label: formatAmount(mark.quantity, mark.unit),
    }));
    if (photo) {
      return <ReportMarkedFigure src={photo.url} photo={photo} width={width} marks={figureMarks} />;
    }
    const drawing = getApplicationFigure(view.key, variant);
    return <ReportMarkedFigure src={drawing.src} frame={drawing} width={width} marks={figureMarks} />;
  }

  return (
    <>
      {totals.length > 0 && (
        <div className={paper.section}>
          <span className={paper.sectionTitle}>Totais da sessão</span>
          <table className={paper.table}>
            <thead>
              <tr>
                <th>Produto</th>
                <th className={paper.num}>Marcações</th>
                <th className={paper.num}>Total</th>
              </tr>
            </thead>
            <tbody>
              {totals.map((total) => (
                <tr key={`${total.product}|${total.unit}`}>
                  <td><span className={paper.dot} style={{ background: total.color }} />{total.product}</td>
                  <td className={paper.num}>{total.count}</td>
                  <td className={paper.num}>{formatAmount(total.total, total.unit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {views.map((view) => (
        <div key={view.key} className={`${paper.section} ${styles.view}`}>
          <span className={paper.sectionTitle}>{view.label}</span>
          <div className={styles.layout}>
            {renderFigure(view)}
            <table className={paper.table}>
              <thead>
                <tr>
                  <th />
                  <th>Produto</th>
                  <th className={paper.num}>Quantidade</th>
                  <th>Tipo</th>
                  <th>Nota</th>
                </tr>
              </thead>
              <tbody>
                {view.items.map(({ mark, number }) => (
                  <tr key={number}>
                    <td><span className={paper.number} style={{ background: mark.color }}>{number}</span></td>
                    <td>{mark.product}</td>
                    <td className={paper.num}>{formatAmount(mark.quantity, mark.unit)}</td>
                    <td>{markKindLabel(mark.kind)}</td>
                    <td className={styles.note}>{mark.note || missing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {record && (record.batch || record.notes) && (
        <div className={paper.noteBox}>
          {record.batch && (
            <>
              <span className={paper.sectionTitle}>Lote dos produtos</span>
              <div className={`${paper.noteText} ${styles.batch}`}>{record.batch}</div>
            </>
          )}
          {record.notes && (
            <>
              <span className={paper.sectionTitle}>Observações da sessão</span>
              <div className={paper.noteText}>{record.notes}</div>
            </>
          )}
        </div>
      )}
    </>
  );
};

/** PDF do mapa de aplicação de um atendimento: papel + miolo. */
const ApplicationMapReportTemplate = forwardRef(({ companyData, professional, patientName, ...job }, ref) => {
  const meta = applicationMapReportMeta(job);
  return (
    <ReportPaper
      ref={ref}
      companyData={companyData}
      professional={professional}
      patientName={patientName}
      title="Mapa de aplicação"
      subtitle={meta.subtitle}
      date={meta.date}
    >
      <ApplicationMapReportBody {...job} />
    </ReportPaper>
  );
});

ApplicationMapReportTemplate.displayName = 'ApplicationMapReportTemplate';

export default ApplicationMapReportTemplate;
